import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { BoundedContent, Box } from '@vp/swan';
import { AnonymousOrderLookupFormDisplay } from '../anonymousOrders/AnonymousOrderLookupFormDisplay';
import useSiteData from '../../../hooks/useSiteData';
import DebugTools from '../../debugTooling/DebugTools';
import { DebugModeContext } from '../../contexts/DebugModeContext';
import useIdentityContext from '../../../hooks/useIdentityContext';
import {
  GET_ANONYMOUS_FEEDBACK_GROUP,
  GET_ANONYMOUS_ORDER_DETAILS,
} from '../../../constants/reactQueryKeys';
import { getAnonymousOrderDetails } from '../../../apis/bopApi';
import { getFeedbackGroupForAnonymousOrder } from '../../../apis/orderUpdateRequestsApi';
import DesignFeedbackErrorPage from './DesignFeedbackErrorPage';
import { BasicPreloader } from '../../shared/BasicPreloader';
import AnonErrorHandler from '../../shared/AnonErrorHandler';
import { DesignFeedbackRequestGroupProvider } from '../../contexts/DesignFeedbackGroupRequestContext';
import { DesignFeedbackGroupResponseProvider } from '../../contexts/DesignFeedbackGroupResponseContext';
import DesignFeedbackGroupPage from './DesignFeedbackGroupPage';
import { customerFeedbackGroupStatesFilter } from '../../../constants/customerFeedbackGroupStates';

export const AnonymousDesignFeedbackGroupApplication = ({
  orderIdOnPath,
  groupIdOnPath,
}) => {
  const { auth } = useIdentityContext();
  const { debugEnabled } = useContext(DebugModeContext);
  const {
    tenantEnvironment,
    vistacartTenant,
    externalAppNavigation: nav,
  } = useSiteData();

  const orderDetailsUrl = nav.orderDetails;

  const [email, setEmail] = useState('');
  const [orderNumber, setOrderNumber] = useState(orderIdOnPath);
  const groupId = groupIdOnPath;

  // Fetch the anonymous order using the shopper's email
  const {
    isLoading: isOrderFetchLoading,
    isError: isOrderFetchFailed,
    isSuccess: isOrderFetchSuccess,
    data: order,
    error: orderFetchError,
  } = useQuery(
    [GET_ANONYMOUS_ORDER_DETAILS, email, orderNumber],
    () =>
      getAnonymousOrderDetails({
        email,
        orderNumber,
        environment: tenantEnvironment,
        vistacartTenant,
      }),
    {
      enabled: !!email && !!orderNumber,
    }
  );

  const shopperId = order?.shopperId;
  const orderId = order?.orderNumber;
  const orderPlacedAnonymously = order?.orderPlacedAnonymously;

  // Fetch the design feedback group on the anonymous order
  const {
    isLoading: isDesignFeedbackRequestGroupLoading,
    isError: isDesignFeedbackRequestGroupFailed,
    data: designFeedbackRequestGroup,
    error: designFeedbackRequestGroupFetchError,
  } = useQuery(
    [GET_ANONYMOUS_FEEDBACK_GROUP, email, orderNumber, groupId],
    async () =>
      getFeedbackGroupForAnonymousOrder({
        orderNumber,
        email,
        groupId,
        environment: tenantEnvironment,
        states: customerFeedbackGroupStatesFilter,
      }),
    {
      enabled: isOrderFetchSuccess && orderPlacedAnonymously,
    }
  );

  // While the order or design feedback request is being fetched, show a preloader
  if (isOrderFetchLoading || isDesignFeedbackRequestGroupLoading) {
    return <BasicPreloader isCentered my={9} />;
  }

  // If the order fetch fails, show an error page
  if (isOrderFetchFailed) {
    // 4xx not found
    if (
      orderFetchError?.response?.status === 400 ||
      orderFetchError?.response?.status === 403 ||
      orderFetchError?.response?.status === 404
    ) {
      return <AnonErrorHandler error={orderFetchError} />;
    }
    // All other errors
    return <DesignFeedbackErrorPage />;
  }

  // If the design feedback request fetch fails...
  if (isDesignFeedbackRequestGroupFailed) {
    // 404 request not found -> redirect to order details
    if (designFeedbackRequestGroupFetchError?.response?.status === 404) {
      window.location.assign(`${orderDetailsUrl}?orderId=${orderId}`);
      return <BasicPreloader isCentered size="super" mt="xl" mb="xl" />;
    }
    // All other errors -> show an error page
    return <DesignFeedbackErrorPage />;
  }

  // If an order hasn't been fetched yet, show order lookup form
  if (!order) {
    return (
      <AnonymousOrderLookupFormDisplay
        setEmail={setEmail}
        setOrderNumber={setOrderNumber}
        orderNumber={orderNumber}
      />
    );
  }

  // If the fetched order was not placed anonymously, then we need to force this user to login to view it.
  if (!orderPlacedAnonymously) {
    // Push design feedback app URL to history stack so the user is redirected after sign-in
    if (orderId && groupId) {
      const url = new URL(window.location);
      url.searchParams.append('orderId', orderId);
      url.searchParams.append('groupId', groupId);
      url.searchParams.append('action', 'df');
      window.history.pushState({}, '', url);
    }
    auth.signIn();
    return null;
  }

  // we do not care If the request is pending or processing, just show the feedback page
  return (
    <>
      {debugEnabled && <DebugTools shopperId={shopperId} order={order} />}
      <DesignFeedbackRequestGroupProvider
        order={order}
        orderId={orderId}
        groupId={groupId}
        request={designFeedbackRequestGroup}
      >
        <DesignFeedbackGroupResponseProvider>
          <BoundedContent>
            <Box my={7}>
              <DesignFeedbackGroupPage />
            </Box>
          </BoundedContent>
        </DesignFeedbackGroupResponseProvider>
      </DesignFeedbackRequestGroupProvider>
    </>
  );
};

AnonymousDesignFeedbackGroupApplication.propTypes = {
  orderIdOnPath: PropTypes.string.isRequired,
  groupIdOnPath: PropTypes.string.isRequired,
};
